import { SubAccountMinimal } from "app/data/account/SubAccount";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { PAGE_IDS } from "app/data/common/pageIds";
import CompanyService from "app/data/company/companyService";
import { SearchRequesterModel, SearchRequestersResponseModel } from "app/data/company/models";
import { QuickPickupCreateModel, QuickPickupCreateSchema, QuickPickupItemPackageTypeOptions } from "app/data/pickup/models";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import { currentOrClosestBusinessDay, makeId } from "hooks/utils";
import debounce from "lodash/debounce";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useSelector, useDispatch } from "react-redux";
import { fromTimeOptions, toTimeOptions } from "services/common/time";
import { getDateClassName, getNonServiceDateNotification } from "services/pickups";
import { commonSelector, getPickupDays } from "slices/common/commonSlice";
import { addShipper, checkPickup, clearShippers, createQuickPickup, getShippers, pickupSelector, searchCompany, setPaypointNumber, setShippersSearch, setTeamName } from "slices/pickup/pickupSlice";
import { shipperSelector } from "slices/shipper/shipperSlice";
import ContentContainer from "templates/content-container/contentContainer";
import Button, { ButtonThemes } from "ui-components/button/button";
import { Card } from "ui-components/card/card";
import XGSFormDate from "ui-components/form/date/xgsFormDate";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import ImportantMessage, { ImportantMessageTheme } from "ui-components/molecules/important-message/importantMessage";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import ShipperCreate from "ui-components/shipper-create/shipperСreate";
import XGSPopup, { PopupTheme } from "ui-components/xgs-popup/xgsPopup";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import Loading from "ui-components/loading/loading";
import { searchZipcodeLocations, zipcodeLocationsSelector } from "slices/zipcode-locations/zipcodeLocationsSlice";
import XGSFormInput from "ui-components/form/input/xgsFormInput";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import "./pickupCreateQuick.scss";
import Collapsible from "ui-components/collapsible/collapsible";
import { toast } from "react-toastify";

let initialValues: QuickPickupCreateModel = {
    teamId: "",
    shipper: {
        id: null,
        accountNumber: null,
        name: "",
        address: {
            address1: "",
            city: "",
            postalCode: "",
            state: "",
            additionalAddressLine: "",
        },
        contacts: {
            email: ""
        }
    },
    bolNumber: "",
    poNumber: "",
    additionalReference: "",
    specialInstructions: "",
    pickupDate: "",
    pickupReadyTime: undefined,
    pickupCloseTime: undefined,
    items: [{
        packageType: "",
        units: null,
        weight: null,
        width: null
    }],
    consigneeLocation: "",
    consignee: {
        address: {
            city: "",
            state: "",
            postalCode: ""
        }
    }
}

const companyService = CompanyService.getInstance();

const PickupCreateQuick: React.FC<{}> = () => {
    const pickupState = useSelector(pickupSelector);
    const commonState = useSelector(commonSelector);
    const shipperState = useSelector(shipperSelector);
    const zipcodeLocationsSate = useSelector(zipcodeLocationsSelector);
    const dispatch = useDispatch();
    const pickupFormRef = useRef<FormikProps<QuickPickupCreateModel>>(null);
    const [team, setTeam] = useState<XGSSelectOption | null | undefined>();
    const [shipperFormValue, setShipperFormValue] = useState<XGSSelectOption | null>();
    const [pickupAvailable, setPickupAvailable] = useState(true);
    const [shipperCreated, setShipperCreated] = useState(false);
    const [shipperQuery, setShipperQuery] = useState("");
    const [nonServiceDayNotification, setNonServiceDayNotification] = useState("");
    const [formInitialValues, setFormInitialValues] = useState<QuickPickupCreateModel>(initialValues);
    const [destinationLocationFormValue, setDestinationLocationFormValue] = useState<{ value: any, label: string, subtitle?: string } | null>();

    const shippers = shipperQuery ? pickupState.shippersSearch : pickupState.shippers;

    let companySearch = (value: string) => {
        value?.length > 1 && dispatch(searchCompany(value));
    }

    const convertToAmPm = (time: string | undefined) => {
        if (!time) return undefined;

        // If already in AM/PM format (case-insensitive), skip conversion
        const lowerTime = time.toLowerCase().trim();
        if (lowerTime.includes("am") || lowerTime.includes("pm")) {
            return lowerTime;
        }

        // Handle 24-hour time like "16:30" → "4:30 pm"
        const [hourStr, minute = "00"] = time.split(":");
        let hour = parseInt(hourStr, 10);

        // If not a valid number (e.g., text like "--" or ""), return as is
        if (isNaN(hour)) return time;

        const ampm = hour >= 12 ? "pm" : "am";
        hour = hour % 12 || 12; // 0 -> 12, 13 -> 1, etc.

        return `${hour}:${minute} ${ampm}`;
    };

    useEffect(() => {
        // build fresh values whenever pickupState updates
        const freshValues: QuickPickupCreateModel = {
            teamId: pickupState.teamId || "",
            shipper: {
                id: pickupState.shipper.id || null,
                accountNumber: pickupState.shipper.accountNumber || null,
                name: pickupState.shipper.name || "",
                address: {
                    address1: pickupState.shipper.address.address1 || "",
                    city: pickupState.shipper.address.city || "",
                    postalCode: pickupState.shipper.address.postalCode || "",
                    state: pickupState.shipper.address.state || "",
                    additionalAddressLine: pickupState.shipper.address.additionalAddressLine || "",
                },
                contacts: {
                    email: ""
                }
            },
            bolNumber: pickupState.bolNumber || "",
            poNumber: pickupState.poNumber || "",
            additionalReference: pickupState.additionalReference || "",
            specialInstructions: pickupState.specialInstructions || "",
            pickupDate: pickupState?.recognizedBolData?.pickupWindow?.date || pickupState.pickupDate || "",
            pickupReadyTime: convertToAmPm(pickupState?.recognizedBolData?.pickupWindow?.startTime) || "",
            pickupCloseTime: convertToAmPm(pickupState?.recognizedBolData?.pickupWindow?.endTime) || "",
            items: [{
                packageType: "",
                units: null,
                weight: null,
                width: null
            }],
            consigneeLocation: "",
            consignee: {
                address: {
                    city: "",
                    state: "",
                    postalCode: ""
                }
            }
        };
        setFormInitialValues(freshValues);
    }, [pickupState]);

    companySearch = debounce(companySearch, 300);

    const changeTeam = (_team: XGSSelectOption | null | undefined) => {
        setTeam(_team);
        _team && dispatch(setTeamName(_team.label));
        const selectedTeamId = _team ? _team.value : null;
        selectedTeamId && dispatch(getShippers(selectedTeamId.toString()));
        pickupFormRef.current?.setFieldValue("teamId", selectedTeamId);
    };

    const resetShipper = (_team: XGSSelectOption | null | undefined) => {
        setShipperFormValue(null);
        dispatch(clearShippers());
        pickupFormRef.current?.setFieldValue("shipper.id", undefined);
    }

    const onChangeTeam = (_team: XGSSelectOption | null | undefined) => {
        changeTeam(_team);
        resetShipper(_team);
    };

    const shipperOption = (shipper: SubAccountMinimal) => ({
        label: `${shipper.name} (${shipper.accountNumber})`,
        subtitle: `
          ${(shipper.address?.line1 && (shipper.address?.line1 !== "-")) ? shipper.address?.line1.toLowerCase().capitalize() + ", " : ""}
          ${(shipper.address?.city && (shipper.address?.city !== "-")) ? shipper.address?.city.toLowerCase().capitalize() : ""}${(shipper.address?.city && (shipper.address?.city !== "-") && shipper.address?.state && (shipper.address?.state !== "-")) ? ", " : ""}${(shipper.address?.state && (shipper.address?.state !== "-")) ? shipper.address?.state : ""}
        `,
        value: `${shipper.id}`
    });

    const shipperOptions = useMemo(() => {
        let options: XGSSelectOption[] = [];
        if (pickupState.shippers && pickupState.shippers.length > 0) {
            for (let i = 0; i < pickupState.shippers.length; i++) {
                if (!pickupState.shippers[i].isNew && pickupState.shippers[i].id) {
                    options.push(shipperOption(pickupState.shippers[i]));
                }
            }
        }
        if (options.length > 0) {
            options = uniqBy(options, "value");
            options = sortBy(options, ["label"]);
        }
        return options;
    }, [pickupState.shippers]);

    const groupOptions = (accountOptions: XGSSelectOption[], globalOptions?: XGSSelectOption[]) => ([
        {
            label: "Results related to requester:",
            options: accountOptions,
        },
        {
            label: "All results:",
            options: globalOptions || [],
        }
    ]);

    const groupedShipperOptions = useMemo(() => groupOptions(shipperOptions), [shipperOptions]);

    const requesterOption = (company: SearchRequesterModel) => ({
        label: company.name,
        subtitle: company.accountName ? `${company.accountName} (${company.accountNumber})` : null,
        value: company.id,
    });

    const setSelectedShipper = (shipper?: SubAccountMinimal | null) => {
        pickupFormRef.current?.setFieldValue("shipper.id", shipper?.id || undefined);
        pickupFormRef.current?.setFieldValue("shipper.accountNumber", shipper?.accountNumber || undefined);
        pickupFormRef.current?.setFieldValue("shipper.name", shipper?.name || undefined);
        pickupFormRef.current?.setFieldValue("shipper.address.address1", shipper?.address?.line1 || undefined);
        pickupFormRef.current?.setFieldValue("shipper.address.city", shipper?.address?.city || undefined);
        pickupFormRef.current?.setFieldValue("shipper.address.postalCode", shipper?.address?.zip || undefined);
        pickupFormRef.current?.setFieldValue("shipper.address.state", shipper?.address?.state || undefined);
        pickupFormRef.current?.setFieldValue("shipper.address.additionalAddressLine", shipper?.address?.additionalAddressLine || undefined);
        pickupFormRef.current?.setFieldValue("pickupDate", "");
        setNonServiceDayNotification("");
    };

    const onChangeTeamAccount = (account: XGSSelectOption | null | undefined) => {

        setShipperFormValue(account);

        const shipper = shippers && shippers.find(obj => obj.id === account?.value);

        if (shipper?.address?.zip) {
            checkPickupAvailability(shipper?.address.zip);
        }

        if (shipper?.accountNumber) {
            dispatch(getPickupDays(shipper?.accountNumber, shipper?.address?.zip));
        }

        setSelectedShipper(shipper);

        if (!team && shipper?.teamId) {
            changeTeam({ value: shipper.teamId, label: shipper.teamName || "-" });
        }
    };

    const onAddShipper = () => {
        const shipper: SubAccountMinimal = {
            ...shipperState.shipper,
            address: {
                line1: shipperState.shipper.address.address1,
                line2: "",
                city: shipperState.shipper.address.city,
                state: shipperState.shipper.address.state,
                zip: shipperState.shipper.address.postalCode,
                country: "",
                additionalAddressLine: shipperState.shipper.address.additionalAddressLine,
            },
            id: makeId(5),
            accountNumber: 69,
            isNew: true,
        };

        dispatch(addShipper(shipper));

        const option = shipperOption(shipper);
        setShipperFormValue(option);

        if (shipper?.accountNumber) {
            dispatch(getPickupDays(shipper?.accountNumber, shipper.address?.zip));
        }

        setSelectedShipper(shipper);
        setShipperCreated(false);
    }

    const onClearForm = () => {
        setTeam(null);
        setShipperFormValue(null);
        setPickupAvailable(true);
        setShipperCreated(false);
        setShipperQuery("");
        setNonServiceDayNotification("");
        setDestinationLocationFormValue(null);
        pickupFormRef.current?.resetForm();
    }

    const onSubmit = (data: QuickPickupCreateModel) => {
        const payload = {
            teamId: data.teamId,
            shipper: {
                ...data.shipper,
                contacts: {
                    email: ""
                }
            },
            ...(data.bolNumber && { bolNumber: data.bolNumber }),
            ...(data.poNumber && { poNumber: data.poNumber }),
            ...(data.additionalReference && { additionalReference: data.additionalReference }),
            pickupDate: data.pickupDate.toApiDateFormat(),
            pickupReadyTime: data.pickupReadyTime || "",
            pickupCloseTime: data.pickupCloseTime || "",
            specialInstructions: data.specialInstructions,
            consignee: {
                address: { 
                    ...data.consigneeLocation.value,
                    postalCode: data.consigneeLocation.value.zip
                }
            },
            items: data.items,
            consigneeLocation: data.consigneeLocation
        };

        dispatch(createQuickPickup(
            payload,
            (pickupNumber) => {
                onClearForm();
                toast.info(`Pickup ${pickupNumber} created successfully!`);
                mixpanel.track("pickup:quick_pickup_created");
            },
            (error) => {
                let errorString = (error) ? error : "Internal Server Error, re-try the action or contact support";
                if (typeof errorString !== 'string') errorString = JSON.stringify(errorString);
                toast.error(error);
            }
        ));
    };

    const checkPickupAvailability = (zip: string) => {
        zip && dispatch(checkPickup(zip, () => setPickupAvailable(true), () => setPickupAvailable(false)));
    };

    const getSearchOptions = (shippers: SubAccountMinimal[]) => groupOptions(
        shippers
            .filter(shipper => shipper.paypointNumber === pickupState.paypointNumber)
            .map(shipperOption),
        shippers
            .filter(shipper => shipper.paypointNumber !== pickupState.paypointNumber)
            .map(shipperOption),
    );

    // eslint-disable-next-line
    const searchShipper = React.useCallback(
        debounce((query, callback) => {
            companyService.getShippersGlobalSearch(query, pickupState.paypointNumber)
                .then(options => {
                    const shippers = options.map(option => ({ ...option, id: makeId(5) }));
                    dispatch(setShippersSearch(shippers));
                    callback(getSearchOptions(shippers)
                    )
                })
        }, 300),
        [pickupState.paypointNumber]
    );

    const getRequesterSearchOptions = (search: SearchRequestersResponseModel | null) => {
        if (!search) return [];

        const requestersOptions = search.teams.map(requesterOption);
        const accountsOptions = search.accounts.map(requesterOption);

        return [{
            label: "By Company:",
            options: requestersOptions || [],
        },
        {
            label: "By Account:",
            options: accountsOptions || [],
        }]
    }

    // eslint-disable-next-line
    const searchRequester = React.useCallback(
        debounce((query, callback) => {
            companyService.getRequestersGlobalSearch(query)
                .then(options => {
                    callback(getRequesterSearchOptions(options))
                })
        }, 300),
        []
    );

    const checkNonServiceDateNotification = (date: string) => {
        const notification = getNonServiceDateNotification(
            date,
            commonState.pickupDays,
            commonState.shipperHolidays.map(holiday => holiday.date),
            commonState.xgsHolidays.map(holiday => holiday.date),
        );

        setNonServiceDayNotification(notification);
    };

    const getHolidayDates = () => [...commonState.shipperHolidays, ...commonState.xgsHolidays].map((holiday) => holiday.date);

    const safeTime = (time?: string | null) => {
        if (!time || time.trim() === "") return undefined;
        try {
            return time.toUiTimeFormat?.() || time;
        } catch {
            return undefined;
        }
    };

    const safeDate = (date?: string | null) => {
        if (!date || date.trim() === "") return "";
        return date;
    };

    useEffect(() => {
        if (pickupState.teamId) {
            initialValues.teamId = pickupState.teamId;
            setTeam({
                value: pickupState.teamId,
                label: pickupState.teamName
            });
        }
        if (pickupState.shipper.id) {
            initialValues.shipper.id = pickupState.shipper.id;
            let shipperObj = pickupState.shippers?.find(obj => obj.id === pickupState.shipper.id)
                || pickupState.shippersSearch?.find(obj => obj.id === pickupState.shipper.id);
            shipperObj && setShipperFormValue(shipperOption(shipperObj));
        }
        initialValues.shipper.accountNumber = pickupState.shipper.accountNumber;
        initialValues.shipper.name = pickupState.shipper.name;
        initialValues.shipper.address.address1 = pickupState.shipper.address.address1;
        initialValues.shipper.address.city = pickupState.shipper.address.city;
        initialValues.shipper.address.postalCode = pickupState.shipper.address.postalCode;
        initialValues.shipper.address.state = pickupState.shipper.address.state;
        initialValues.shipper.address.additionalAddressLine = pickupState.shipper.address.additionalAddressLine;
        initialValues.bolNumber = pickupState.bolNumber;
        initialValues.poNumber = pickupState.poNumber;
        initialValues.additionalReference = pickupState.additionalReference;
        initialValues.specialInstructions = pickupState.specialInstructions;

        // date
        const date = safeDate(pickupState?.recognizedBolData?.pickupWindow?.date);
        initialValues.pickupDate = date;
        pickupFormRef.current?.setFieldValue("pickupDate", date);
        checkNonServiceDateNotification(pickupState?.pickupDate);

        // ready time
        const readyTime = safeTime(pickupState?.recognizedBolData?.pickupWindow?.startTime);
        initialValues.pickupReadyTime = readyTime;
        if (readyTime) {
            pickupFormRef.current?.setFieldValue("pickupReadyTime", convertToAmPm(readyTime));
        }

        // close time
        const closeTime = safeTime(pickupState?.recognizedBolData?.pickupWindow?.endTime);
        initialValues.pickupCloseTime = closeTime;
        if (closeTime) {
            pickupFormRef.current?.setFieldValue("pickupCloseTime", convertToAmPm(closeTime));
        }

        setTimeout(() => {
            pickupFormRef.current?.validateForm();
        }, 50);
        // eslint-disable-next-line
    }, [pickupState.recognizedBolData]);

    useEffect(() => {
        if (!pickupState.populatedFromBol || pickupState.teamId) return;
        // set team and shipper based on recognized values
        if (pickupState.shipper.name) {
            companyService.getRequestersGlobalSearch(pickupState.shipper.name)
                .then(result => {
                    if (result && result.accounts.length > 0) {
                        setTeam({
                            label: result.accounts[0].name,
                            value: result.accounts[0].id
                        });
                        pickupFormRef.current?.setFieldValue("teamId", result.accounts[0].id);
                        dispatch(setTeamName(result.accounts[0].name));
                        dispatch(getShippers(result.accounts[0].id, (shippers: SubAccountMinimal[]) => {
                            const matchedShippers = shippers.filter(shipper => (shipper.name === pickupState.shipper.name) && (shipper.address?.zip === pickupState.shipper.address.postalCode));
                            if (matchedShippers.length === 0) return;
                            const exactShipper = (matchedShippers.length > 1)
                                ? matchedShippers.filter(shipper => shipper.address?.line1 === pickupState.shipper.address.address1)[0]
                                : matchedShippers[0];
                            if (exactShipper) {
                                onChangeTeamAccount(shipperOption(exactShipper));
                                setSelectedShipper(exactShipper);
                            }
                        }));
                    }
                });
        }
        initialValues.bolNumber = pickupState.bolNumber;
        initialValues.poNumber = pickupState.poNumber;
        initialValues.specialInstructions = pickupState.specialInstructions;

        // date
        const date = safeDate(pickupState?.recognizedBolData?.pickupWindow?.date);
        initialValues.pickupDate = date;
        pickupFormRef.current?.setFieldValue("pickupDate", date);
        // setPickupDateFormValue(date);
        checkNonServiceDateNotification(pickupState?.pickupDate);

        // ready time
        const readyTime = safeTime(pickupState?.recognizedBolData?.pickupWindow?.startTime);

        initialValues.pickupReadyTime = readyTime;
        if (readyTime) {
            pickupFormRef.current?.setFieldValue("pickupReadyTime", convertToAmPm(readyTime));
        }

        // close time
        const closeTime = safeTime(pickupState?.recognizedBolData?.pickupWindow?.endTime);
        initialValues.pickupCloseTime = closeTime;
        if (closeTime) {
            pickupFormRef.current?.setFieldValue("pickupCloseTime", convertToAmPm(closeTime));
        }

        setTimeout(() => {
            pickupFormRef.current?.validateForm();
        }, 50);
        // eslint-disable-next-line
    }, [pickupState.populatedFromBol]);

    useEffect(() => {
        // preselect shipper if it is alone in dropdown
        if (pickupState.shippers === null || pickupState.shipper) return;
        if (shipperOptions.length === 1) {
            onChangeTeamAccount(shipperOptions[0]);
        }
        // eslint-disable-next-line
    }, [pickupState.shippers, pickupState.shipper, shipperOptions]);


    useEffect(() => {
        if (!team) {
            dispatch(setPaypointNumber(""));
        }
    }, [team, dispatch]);

    let destinationLocationSearch = (query: string) => {
        query?.length > 1 && dispatch(searchZipcodeLocations(query));
    }
    destinationLocationSearch = debounce(destinationLocationSearch, 300);

    return (
        <ContentContainer pageId={PAGE_IDS.CREATE_QUICK_PICKUP} title="New Pickup">
            <div className="xgs-pickup-create-quick">
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize
                    validationSchema={QuickPickupCreateSchema}
                    onSubmit={onSubmit}
                    innerRef={pickupFormRef}
                >
                    {(props: FormikProps<QuickPickupCreateModel>) => (
                        <Form className="xgs-pickup-create-quick__form">
                            <div className="xgs-pickup-create-quick__form__details">
                                <div className="xgs-pickup-create-quick__form__details__section">
                                    <div className="xgs-pickup-create-quick__form__details__section__header">
                                        Shipper Information
                                        {pickupState.requestStarted && pickupState.requestCreator === "CHECK_PICKUP" && <Loading isLoading={true} className="xgs-pickup-create-quick__form__details__section__header__loader" />}
                                    </div>
                                    <LabeledSelectInput
                                        isDisabled={false}
                                        isLoading={pickupState.requestStarted && pickupState.requestCreator === "SEARCH_COMPANY"}
                                        label="Requester (Company):"
                                        labelMode={LabelModes.column}
                                        onInputChange={companySearch}
                                        onValueChange={onChangeTeam}
                                        openMenuOnClick={pickupState.companies?.length > 0}
                                        options={pickupState.companies}
                                        required={true}
                                        requiredAsteriskDisabled={false}
                                        placeholder="Enter company name..."
                                        value={team}
                                        isClearable
                                        async
                                        cacheOptions={false}
                                        loadOptions={searchRequester}
                                        formik={true}
                                        className="xgs-pickup-create-quick__form__details__section__field"
                                        isOptionSelected={(option, selectValue) => (option.value === selectValue[0]?.value && option.label === selectValue[0]?.label && option.subtitle === selectValue[0]?.subtitle)}
                                    />
                                    <LabeledSelectInput
                                        name="shipper"
                                        label="Pickup location (Shipper):"
                                        labelMode={LabelModes.column}
                                        value={shipperFormValue}
                                        onValueChange={onChangeTeamAccount}
                                        onInputChange={setShipperQuery}
                                        options={groupedShipperOptions}
                                        async
                                        cacheOptions={false}
                                        loadOptions={searchShipper}
                                        required={true}
                                        requiredAsteriskDisabled={false}
                                        formik={true}
                                        className="xgs-pickup-create-quick__form__details__section__field"
                                        isLoading={pickupState.requestStarted && pickupState.requestCreator === "GET_SHIPPERS"}
                                        isClearable
                                        extendAction={pickupState.paypointNumber && shipperQuery ? () => setShipperCreated(true) : undefined}
                                        extendLabel="Add new shipper"
                                        placeholder="Start typing..."
                                    />
                                    {shipperFormValue && (<Card
                                        icon={(
                                            <div className="shipper-card__icon">
                                                <XGSIcon className="card__info__item" icon={XGSIcons.faMapPin} />
                                            </div>
                                        )}
                                        title="Shipper Location"
                                        hideButtons
                                    >
                                        <div className="xgs-pickup-create-quick__form__details__section__address-block">
                                            <div>Address: <span>{props.values.shipper.address.address1}</span></div>
                                            <div>City: <span>{props.values.shipper.address.city}</span></div>
                                            <div>State: <span>{props.values.shipper.address.state}</span></div>
                                            <div>Zip: <span>{props.values.shipper.address.postalCode}</span></div>
                                            {props.values.shipper.address.additionalAddressLine && <div>Additional address line: <span>{props.values.shipper.address.additionalAddressLine}</span></div>}
                                        </div>
                                    </Card>
                                    )}
                                    {shipperFormValue && !pickupAvailable && !(pickupState.requestStarted && pickupState.requestCreator === "CHECK_PICKUP") && (
                                        <ImportantMessage theme={ImportantMessageTheme.RED} mix="xgs-pickup-create-quick__form__details__section__message-row">
                                            The pickup request option isn't available for&nbsp;the&nbsp;current shipper!
                                        </ImportantMessage>
                                    )}
                                </div>
                                <div className="xgs-pickup-create-quick__form__details__section">
                                    <div className="xgs-pickup-create-quick__form__details__section__header">
                                        Pickup Details
                                        {pickupState.requestStarted && pickupState.requestCreator === "CHECK_PICKUP" && <Loading isLoading={true} className="xgs-pickup-create-quick__form__details__section__header__loader" />}
                                    </div>
                                    <XGSFormDate
                                        className="xgs-pickup-create-quick__form__details__section__field"
                                        name="pickupDate"
                                        label="Pickup Date:"
                                        placeholder="Select date..."
                                        value={props.values.pickupDate}  // <-- use Formik directly
                                        onDateChange={(date) => {
                                            props.setFieldValue("pickupDate", date);
                                            checkNonServiceDateNotification(date);
                                        }}
                                        onChange={() => null}
                                        minDate={new Date(currentOrClosestBusinessDay())}
                                        required={true}
                                        labelMode={LabelModes.column}
                                        disableWeekends={true}
                                        disabled={!shipperFormValue || (!pickupAvailable && !shipperCreated) || (commonState.requestStarted && (commonState.requestCreator === "GET_PICKUP_DAYS"))}
                                        dayClassName={(date) => getDateClassName(date, commonState.pickupDays, getHolidayDates())}
                                    />
                                    {nonServiceDayNotification &&
                                        <ImportantMessage theme={ImportantMessageTheme.BLUE} mix="xgs-pickup-create-quick__form__details__section__message-row">
                                            {nonServiceDayNotification}
                                        </ImportantMessage>
                                    }
                                    <div className="xgs-pickup-create-quick__form__details__section__field xgs-pickup-create-quick__form__details__section__availability-controls">
                                        <LabeledSelectInput
                                            name="pickupReadyTime"
                                            value={
                                                props.values.pickupReadyTime
                                                    ? { value: props.values.pickupReadyTime, label: props.values.pickupReadyTime }
                                                    : null
                                            }
                                            onValueChange={(v) => props.setFieldValue("pickupReadyTime", v?.value || "")}
                                            label="Pickup Ready:"
                                            isSearchable={false}
                                            labelMode={LabelModes.column}
                                            options={fromTimeOptions()}
                                            required={true}
                                            formik={true}
                                            placeholder="Ready..."
                                            menuPlacement="auto"
                                            className="xgs-pickup-create-quick__form__details__section__availability-controls__field"
                                        />
                                        <LabeledSelectInput
                                            name="pickupCloseTime"
                                            label="Dock close time:"
                                            isSearchable={false}
                                            labelMode={LabelModes.column}
                                            value={
                                                props.values.pickupCloseTime
                                                    ? { value: props.values.pickupCloseTime, label: props.values.pickupCloseTime }
                                                    : null
                                            }
                                            onValueChange={(v) => props.setFieldValue("pickupCloseTime", v?.value || "")}
                                            options={toTimeOptions(props.values.pickupReadyTime || "")}
                                            required={true}
                                            formik={true}
                                            placeholder="Close..."
                                            menuPlacement="auto"
                                            className="xgs-pickup-create-quick__form__details__section__availability-controls__field"
                                        />
                                    </div>
                                </div>
                                <div className="xgs-pickup-create-quick__form__details__section">
                                    <div className="xgs-pickup-create-quick__form__details__section__header">
                                        Consignee Information
                                    </div>
                                    <LabeledSelectInput
                                        className="xgs-pickup-create-quick__form__details__section__field"
                                        isDisabled={false}
                                        isLoading={zipcodeLocationsSate.loading}
                                        label="Delivery ZIP Code(Consignee):"
                                        labelMode={LabelModes.column}
                                        name="consigneeLocation"
                                        onInputChange={(e) => destinationLocationSearch(e)}
                                        onValueChange={(v) => {
                                            const obj: any = v;
                                            if (!obj) return;
                                            props.setFieldValue("consigneeLocation", {
                                                label: obj.label,
                                                value: obj.value
                                            });
                                            setDestinationLocationFormValue({
                                                label: obj.label,
                                                value: obj.value
                                            });
                                        }}
                                        openMenuOnClick={zipcodeLocationsSate.locations.length > 0}
                                        options={zipcodeLocationsSate.locations}
                                        placeholder="Enter ZIP Code..."
                                        value={destinationLocationFormValue}
                                        required={true}
                                    />
                                    <div className="xgs-pickup-create-quick__form__details__section__field">
                                        <Collapsible
                                            header={<div>BOL and PO Number</div>}
                                        >
                                            <div className="xgs-gray-area">
                                                <div className="xgs-pickup-create-quick__row">
                                                    <div className="xgs-pickup-create-quick__row__item">
                                                        <XGSFormInput
                                                            type="text"
                                                            name="bolNumber"
                                                            label="Bill of Lading (BOL):"
                                                            required={false}
                                                            requiredAsteriskDisabled={true}
                                                            labelMode={LabelModes.column}
                                                        />
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__row__item">
                                                        <XGSFormInput
                                                            type="text"
                                                            name="poNumber"
                                                            label="Purchase Order (PO):"
                                                            required={false}
                                                            requiredAsteriskDisabled={true}
                                                            labelMode={LabelModes.column}
                                                        />
                                                    </div>
                                                </div>
                                                <XGSFormInput
                                                    type="text"
                                                    name="additionalReference"
                                                    label="Additional Reference:"
                                                    required={false}
                                                    requiredAsteriskDisabled={true}
                                                    labelMode={LabelModes.column}
                                                    className="xgs-pickup-create-quick__field"
                                                />
                                            </div>
                                        </Collapsible>
                                    </div>
                                </div>
                            </div>
                            <div className="xgs-pickup-create-quick__form__items">
                                <FieldArray name="items">
                                    {({ push, remove }) => (
                                        <>
                                            <div className="xgs-pickup-create-quick__form__items__info">
                                                <div className="xgs-pickup-create-quick__form__items__info__title">
                                                    Items ({props.values.items.length} of 15)
                                                </div>
                                                {props.values.items.length < 15 && (
                                                    <Button
                                                        theme={ButtonThemes.blue}
                                                        onClick={() => push({ packageType: '', weight: null, units: null, width: null })}
                                                        disabled={props.values.items.length > 15}
                                                        className="xgs-pickup-create-quick__form__items__info__add-item-button"
                                                    >
                                                        + Add Item
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="xgs-pickup-create-quick__form__items__table">
                                                <div className="xgs-pickup-create-quick__form__items__table__header">
                                                    <div className="xgs-pickup-create-quick__form__items__table__column--line-item-number">
                                                        Line Item #
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__form__items__table__column">
                                                        Package Type
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__form__items__table__column">
                                                        Units
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__form__items__table__column">
                                                        Total Weight
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__form__items__table__column">
                                                        Width
                                                    </div>
                                                    <div className="xgs-pickup-create-quick__form__items__table__column--remove-item">

                                                    </div>
                                                </div>
                                                {props.values.items.map((item, index) => (
                                                    <div key={index} className="xgs-pickup-create-quick__form__items__table__row">
                                                        <div className="xgs-pickup-create-quick__form__items__table__column--line-item-number">
                                                            {index + 1}
                                                        </div>
                                                        <div className="xgs-pickup-create-quick__form__items__table__column">
                                                            <XGSFormSelect
                                                                name={`items[${index}].packageType`}
                                                                labelMode={LabelModes.column}
                                                                options={QuickPickupItemPackageTypeOptions}
                                                                className="xgs-pickup-create-quick__form__items__table__column__cell"
                                                                isClearable
                                                                isSearchable
                                                                placeholder="Select..."
                                                                onValueChange={(option) => {
                                                                    props.setFieldValue(`items[${index}].packageType`, option?.value ?? "");
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="xgs-pickup-create-quick__form__items__table__column">
                                                            <XGSFormInput
                                                                type="text"
                                                                name={`items[${index}].units`}
                                                                required={true}
                                                                requiredAsteriskDisabled={false}
                                                                labelMode={LabelModes.column}
                                                                className="xgs-pickup-create-quick__form__items__table__column__cell"
                                                                placeholder="Count..."
                                                            />
                                                        </div>
                                                        <div className="xgs-pickup-create-quick__form__items__table__column">
                                                            <XGSFormInput
                                                                type="text"
                                                                name={`items[${index}].weight`}
                                                                required={true}
                                                                requiredAsteriskDisabled={false}
                                                                labelMode={LabelModes.column}
                                                                className="xgs-pickup-create-quick__form__items__table__column__cell"
                                                                placeholder="LBS..."
                                                            />
                                                        </div>
                                                        <div className="xgs-pickup-create-quick__form__items__table__column">
                                                            {(props.values.items[index].packageType === "ROLL") && <XGSFormInput
                                                                type="text"
                                                                name={`items[${index}].width`}
                                                                required={true}
                                                                requiredAsteriskDisabled={false}
                                                                labelMode={LabelModes.column}
                                                                className="xgs-pickup-create-quick__form__items__table__column__cell"
                                                                placeholder="Inches..."
                                                            />}
                                                        </div>
                                                        <div className="xgs-pickup-create-quick__form__items__table__column--remove-item">
                                                            {props.values.items.length > 1 && (
                                                                <XGSIcon
                                                                    icon={XGSIcons.faTrashAlt}
                                                                    onClick={() => remove(index)}
                                                                    title="Remove Line Item"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </FieldArray>
                            </div>
                            <div className="xgs-pickup-create-quick__form__submit">
                                <div className="xgs-pickup-create-quick__form__submit__buttons">
                                    <Button
                                        theme={ButtonThemes.blue}
                                        disabled={!props.isValid || !props.values.pickupDate || (pickupState.requestStarted && pickupState.requestCreator === "CREATE_QUICK_PICKUP")}
                                        spinner={pickupState.requestStarted && pickupState.requestCreator === "CREATE_QUICK_PICKUP"}
                                    >
                                        Create Pickup
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
                <XGSPopup
                    opened={shipperCreated}
                    onClose={() => {
                        setPickupAvailable(true);
                        setShipperCreated(false)
                    }}
                    className="shipper-create-popup"
                    closeOnDocumentClick={false}
                    theme={PopupTheme.NEW}
                >
                    <ShipperCreate
                        onCreate={onAddShipper}
                        onZipChange={(zip) => {
                            setPickupAvailable(true);

                            if (/^\d{5}$/.test(zip)) {
                                checkPickupAvailability(zip);
                            }
                        }}
                        isLoading={pickupState.requestStarted && pickupState.requestCreator === "CHECK_PICKUP"}
                        error={pickupAvailable
                            ? ""
                            : ERROR_MESSAGES.ZIP_PICKUP_UNAVAILABLE}
                    />
                </XGSPopup>
            </div>
        </ContentContainer >
    );
};

export default PickupCreateQuick;